import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check, Eye, Download, Mail, MessageCircle } from "lucide-react";
import api, { formatApiError, downloadPdf } from "../lib/api";
import { PageHeader, DataTable, StatusBadge, fmt, waLink } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const METHODS = ["Transferencia", "Depósito", "Efectivo", "Tarjeta", "Cheque"];

export default function PlatformBilling() {
  const [invoices, setInvoices] = useState([]);
  const [schools, setSchools] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [payInv, setPayInv] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payForm, setPayForm] = useState({ method: "Transferencia", reference: "" });
  const [company, setCompany] = useState(null);

  const load = useCallback(() => {
    const qs = statusFilter === "all" ? "" : `?status=${statusFilter}`;
    api.get(`/platform/invoices${qs}`).then((r) => setInvoices(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/schools").then((r) => setSchools(r.data)).catch(() => {});
    api.get("/platform/settings").then((r) => setCompany(r.data)).catch(() => {});
  }, [statusFilter, formatApiError]);
  useEffect(() => { load(); }, [load]);

  const schoolOf = (inv) => schools.find((s) => s.id === inv.school_id);

  const generate = async () => {
    try {
      const r = await api.post("/platform/invoices/generate", { period });
      toast.success(`${r.data.created} factura(s) generada(s) para ${r.data.period}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pay = async () => {
    try {
      await api.post(`/platform/invoices/${payInv.id}/pay`, payForm);
      toast.success(`Factura ${payInv.number} marcada como PAGADA`);
      setPayInv(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`¿Eliminar factura ${inv.number}?`)) return;
    try {
      await api.delete(`/platform/invoices/${inv.id}`);
      toast.success("Factura eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendPdf = async (inv) => {
    try {
      await downloadPdf(`/platform/invoices/${inv.id}/pdf`, `${inv.number}.pdf`);
      toast.success(`PDF de ${inv.number} descargado`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendEmail = async (inv) => {
    try {
      const r = await api.post(`/platform/invoices/${inv.id}/email`);
      toast.success(`Factura enviada por correo a ${r.data.to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendWhatsApp = (inv) => {
    const school = schoolOf(inv);
    const phone = (school?.phones || [])[0];
    if (!phone) { toast.error("El colegio no tiene teléfono registrado"); return; }
    const msg = `${inv.number} — Factura de suscripción (${inv.period}). Plan ${inv.plan_name}: ${inv.students_count} estudiantes. Total: ${fmt(inv.amount)}.`;
    window.open(waLink(phone, msg), "_blank");
  };

  const actionButtons = (i) => (
    <div className="flex gap-1">
      <Button size="sm" variant="ghost" title="Ver detalle" data-testid={`view-platform-invoice-${i.id}`} onClick={() => setDetail(i)}>
        <Eye className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" title="Descargar PDF" data-testid={`pdf-platform-invoice-${i.id}`} onClick={() => sendPdf(i)}>
        <Download className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" title="Enviar por correo" data-testid={`email-platform-invoice-${i.id}`} onClick={() => sendEmail(i)}>
        <Mail className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" title="Enviar por WhatsApp" className="text-emerald-600" data-testid={`whatsapp-platform-invoice-${i.id}`} onClick={() => sendWhatsApp(i)}>
        <MessageCircle className="w-4 h-4" />
      </Button>
      {i.status === "pendiente" && (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" data-testid={`pay-platform-invoice-${i.id}`} onClick={() => { setPayInv(i); setPayForm({ method: "Transferencia", reference: "" }); }}>
          <Check className="w-4 h-4 mr-1" /> Registrar pago
        </Button>
      )}
      {i.status === "pendiente" && (
        <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`delete-platform-invoice-${i.id}`} onClick={() => remove(i)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );

  const columns = [
    { key: "number", label: "Factura", render: (i) => <span className="font-mono text-xs font-semibold">{i.number}</span> },
    { key: "school_name", label: "Colegio", render: (i) => <span className="font-medium text-slate-900">{i.school_name}</span> },
    { key: "plan_name", label: "Plan" },
    { key: "students_count", label: "Estudiantes" },
    { key: "price_per_student", label: "RD$/Est.", render: (i) => fmt(i.price_per_student) },
    { key: "period", label: "Período", render: (i) => <span className="font-mono text-xs">{i.period}</span> },
    { key: "amount", label: "Monto", render: (i) => <span className="font-semibold">{fmt(i.amount)}</span> },
    { key: "status", label: "Estado", render: (i) => <StatusBadge value={i.status} /> },
    { key: "_a", label: "Acciones", render: actionButtons },
  ];

  return (
    <div data-testid="platform-billing-page">
      <PageHeader title="Facturación a colegios" subtitle="Facturas de suscripción por período (precio × estudiantes activos)">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="platform-invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="pagada">Pagadas</SelectItem>
          </SelectContent>
        </Select>
        <Input type="month" className="w-40" data-testid="billing-period-input" value={period} onChange={(e) => setPeriod(e.target.value)} />
        <Button data-testid="generate-invoices-button" className="bg-blue-800 hover:bg-blue-700" onClick={generate}>
          <Plus className="w-4 h-4 mr-1" /> Generar facturas
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={invoices} testid="platform-invoices-table" />

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="platform-invoice-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{detail.number} <StatusBadge value={detail.status} /></DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {company && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="platform-invoice-issuer">
                    <div className="flex flex-col gap-2">
                      {company.logo_url && (
                        <img src={company.logo_url} alt={company.company_name} className="h-11 w-auto object-contain rounded self-start" data-testid="platform-invoice-logo" />
                      )}
                      <div>
                        {(company.show_name !== false || !company.logo_url) && (
                          <p className="font-bold text-slate-900" data-testid="platform-invoice-issuer-name">{company.company_name}</p>
                        )}
                        <p className="text-xs text-slate-500 font-mono">RNC: {company.rnc || "—"}</p>
                        <p className="text-xs text-slate-500">{company.address}</p>
                        <p className="text-xs text-slate-500">Tel: {(company.phones || []).join(", ")}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-slate-600">
                  <p><span className="text-slate-400">Colegio:</span> {detail.school_name}</p>
                  <p><span className="text-slate-400">Plan:</span> {detail.plan_name}</p>
                  <p><span className="text-slate-400">Período:</span> <span className="font-mono">{detail.period}</span></p>
                  <p><span className="text-slate-400">Emitida:</span> {detail.issue_date}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1">
                  <div className="flex justify-between text-slate-600">
                    <span>Suscripción {detail.plan_name} — {detail.students_count} estudiantes × {fmt(detail.price_per_student)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1">
                    <span>Total</span><span data-testid="platform-detail-total">{fmt(detail.amount)}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button size="sm" variant="outline" data-testid="platform-detail-pdf" onClick={() => sendPdf(detail)}><Download className="w-4 h-4 mr-1" /> PDF</Button>
                  <Button size="sm" variant="outline" data-testid="platform-detail-email" onClick={() => sendEmail(detail)}><Mail className="w-4 h-4 mr-1" /> Correo</Button>
                  <Button size="sm" variant="outline" className="text-emerald-600" data-testid="platform-detail-whatsapp" onClick={() => sendWhatsApp(detail)}><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!payInv} onOpenChange={() => setPayInv(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar pago — {payInv?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">{payInv?.school_name} · {payInv?.plan_name} · <strong>{payInv ? fmt(payInv.amount) : ""}</strong></p>
            <div className="space-y-1.5">
              <Label>Método</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="platform-pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Referencia</Label><Input data-testid="platform-pay-reference" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-platform-pay" onClick={() => setPayInv(null)}>Cancelar</Button>
            <Button data-testid="submit-platform-pay" className="bg-blue-800 hover:bg-blue-700" onClick={pay}>Marcar pagada</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
